import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Components
import Layout from './components/Layout/Layout';
import HomePage from './pages/HomePage';
import EventDetailsPage from './pages/EventDetailsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';

// Lazy loaded components for better performance
const SignageDisplay = lazy(() => import('./components/Signage/SignageDisplay'));
// SignageConfig is now a tab within AdminPage, no standalone route needed

// Hooks
import { useAppStore } from './stores/appStore';
// PWA disabled — not using service workers or install prompts
// import { usePWA, useOffline } from './hooks/usePWA';
import { useWebSocket, useRealTimeEvents } from './hooks/useWebSocket';
import { useAuth } from './hooks/useAuth';

// Services
import { eventAPI } from './services/api';

const App = () => {
  const { i18n } = useTranslation();
  
  // Global state
  const { 
    theme, 
    setEvents, 
    setLoading, 
    setOffline,
    selectedDate,
    user,
    loadPublicSettings,
  } = useAppStore();
  
  // Hooks
  // const { isOnline } = useOffline(); // PWA disabled
  const isOnline = navigator.onLine;
  const { isConnected } = useWebSocket();
  useRealTimeEvents();
  // usePWA(); // PWA disabled
  useAuth(); // Initialize authentication

  // Initialize app
  useEffect(() => {
    const initializeApp = async () => {
      setLoading(true);
      
      try {
        // Load public settings and initial events in parallel
        await Promise.all([
          loadPublicSettings(),
          eventAPI.getEvents().then(setEvents)
        ]);
      } catch (error) {
        console.error('Failed to initialize app:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, [setEvents, setLoading, loadPublicSettings]);

  // Handle offline status
  useEffect(() => {
    setOffline(!isOnline);
  }, [isOnline, setOffline]);

  // Apply theme
  useEffect(() => {
    document.body.className = `theme-${theme}`;
  }, [theme]);

  // Set language
  useEffect(() => {
    if (user?.preferences?.language) {
      i18n.changeLanguage(user.preferences.language);
    }
  }, [user?.preferences?.language, i18n]);

  // Preload critical resources
  useEffect(() => {
    // Preload fonts
    const fontPreloadLink = document.createElement('link');
    fontPreloadLink.rel = 'preload';
    fontPreloadLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap';
    fontPreloadLink.as = 'style';
    document.head.appendChild(fontPreloadLink);

    // Cleanup
    return () => {
      document.head.removeChild(fontPreloadLink);
    };
  }, []);

  return (
    <div className="App min-h-screen bg-gray-50">
      <AnimatePresence mode="wait" initial={false}>
        <Routes>
          {/* Signage route - fullscreen without layout */}
          <Route
            path="/signage"
            element={
              <Suspense fallback={
                <div className="fixed inset-0 bg-black flex items-center justify-center">
                  <div className="text-white text-2xl animate-pulse">Loading Signage Display...</div>
                </div>
              }>
                <SignageDisplay />
              </Suspense>
            }
          />
          
          {/* Admin route — standalone, no public header/footer */}
          <Route path="/admin" element={<AdminPage />} />

          {/* Regular app routes with public layout (header + footer) */}
          <Route
            path="/*"
            element={
              <Layout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/events/:eventId" element={<EventDetailsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/404" element={<NotFoundPage />} />
                  <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
              </Layout>
            }
          />
        </Routes>
      </AnimatePresence>
    </div>
  );
};

export default App;